import i18n from '../i18n';
import { timestampToDate } from './dateTimeUtils';

/**
 * 格式化截止时间（秒级时间戳）
 * - 中文：05-28 14:36 / 05-28 14:36:00
 * - 英文：May 28, 14:36 / May 28, 14:36:00
 * - 西语：28 de mayo, 14:36 / 28 de mayo, 14:36:00
 */
export const formatRevokeDeadline = (deadlineTimestamp: string | number, showSeconds = false): string => {
  const d = timestampToDate(Number(deadlineTimestamp));
  const lang = i18n.language;
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const timePart = showSeconds ? `${hh}:${mi}:${ss}` : `${hh}:${mi}`;
  if (lang.startsWith('zh')) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}-${dd} ${timePart}`;
  }
  const datePart = d.toLocaleDateString(lang, { month: 'long', day: 'numeric' });
  return `${datePart}, ${timePart}`;
};

/**
 * 根据当前语言格式化日期
 * @param date 要格式化的日期
 * @param options 格式化选项
 * @returns 格式化后的日期字符串
 */
export const formatLocalizedDate = (
  date: Date | null,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!date) return '';
  
  const currentLang = i18n.language;
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  
  try {
    return date.toLocaleDateString(currentLang, formatOptions);
  } catch (error) {
    // 如果当前语言不支持，尝试使用语言前缀
    const langPrefix = currentLang.split('-')[0];
    try {
      return date.toLocaleDateString(langPrefix, formatOptions);
    } catch (fallbackError) {
      // 最后回退到默认格式
      return date.toLocaleDateString();
    }
  }
};

/**
 * 根据当前语言格式化时间
 * @param date 要格式化的日期
 * @returns 格式化后的时间字符串 (HH:mm 格式，根据语言显示AM/PM)
 */
export const formatLocalizedTime = (date?: Date): string => {
  if (!date) {
    date = new Date();
  }
  
  const currentLang = i18n.language;
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const displayHour = hours === 0 ? 12 : (hours > 12 ? hours - 12 : hours);
  const timeString = `${displayHour}:${minutes.toString().padStart(2, '0')}`;
  
  // 根据语言设置上午/下午标识，统一放在时间后面，添加空格
  let period: string;
  if (currentLang === 'zh' || currentLang.startsWith('zh-')) {
    period = hours < 12 ? '上午' : '下午';
  } else if (currentLang === 'es' || currentLang.startsWith('es-')) {
    period = hours < 12 ? 'a.m' : 'p.m';
  } else {
    // 英文和其他语言
    period = hours < 12 ? 'AM' : 'PM';
  }
  
  return `${timeString} ${period}`;
};

/**
 * 根据当前语言格式化日期时间（包含日期和时间）
 * @param date 要格式化的日期
 * @returns 格式化后的日期时间字符串
 */
export const formatLocalizedDateTime = (date: Date | null): string => {
  if (!date) return '';
  
  const currentLang = i18n.language;
  const defaultOptions: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false // 24小时制
  };
  
  try {
    return date.toLocaleString(currentLang, defaultOptions);
  } catch (error) {
    // 如果当前语言不支持，尝试使用语言前缀
    const langPrefix = currentLang.split('-')[0];
    try {
      return date.toLocaleString(langPrefix, defaultOptions);
    } catch (fallbackError) {
      // 最后回退到默认格式
      return date.toLocaleString();
    }
  }
};

/**
 * 格式化日期时间为统一的24小时制格式：YYYY/MM/DD HH:mm:ss
 * @param date 要格式化的日期
 * @returns 格式化后的日期时间字符串，格式：YYYY/MM/DD HH:mm:ss
 */
export const formatDateTime24Hour = (date: Date | null): string => {
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};